import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { pdfjs } from "react-pdf";
import { API_URL } from "../config";
import VaultPicker from "../secure/VaultPicker";

import {
  UploadCloud,
  FolderOpen,
  FileText,
  ArrowRight,
  Check,
  RefreshCw,
  Trash2,
  Eye,
  Calendar,
  Bell,
  ChevronDown,
  Info,
  Save,
  ShieldCheck,
  Lock,
  UserCheck,
  Clock,
  ClipboardList,
  Hash,
} from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const STEPS = ["Upload Document", "Add Recipients", "Review & Send"];
const ACCEPTED = ["pdf", "docx", "txt", "xlsx", "png", "jpg", "jpeg"];
const MAX_MB = 100;
const TITLE_MAX = 100;
const MESSAGE_MAX = 500;
const DEFAULT_MESSAGE = "Please review and sign the attached document.";

const REMINDERS = [
  { value: "none", label: "No reminders" },
  { value: "1", label: "Every day" },
  { value: "3", label: "Every 3 days" },
  { value: "7", label: "Every week" },
];

// yyyy-mm-dd in the user's local time (what <input type="date"> expects)
const toDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};

const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

const prettyTitle = (fileName = "") =>
  fileName
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, TITLE_MAX);

const formatUploaded = (d) =>
  d.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

async function sha256Hex(buffer) {
  try {
    const digest = await window.crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null; // e.g. page served over plain http
  }
}

async function countPdfPages(buffer) {
  try {
    const pdf = await pdfjs.getDocument({ data: new Uint8Array(buffer.slice(0)) }).promise;
    const pages = pdf.numPages;
    pdf.destroy();
    return pages;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

const Label = ({ children, required }) => (
  <label className="block text-xs font-medium text-gray-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

// A bordered field with the icon as a normal flex child, so the icon can never sit on top of the text.
const IconField = ({ icon: Icon, chevron, children }) => (
  <div className="flex items-center gap-2 h-9 w-full border border-gray-200 rounded-lg px-3 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
    <Icon size={14} className="text-gray-500 shrink-0" />
    {children}
    {chevron && <ChevronDown size={14} className="text-gray-500 shrink-0 pointer-events-none -ml-[18px]" />}
  </div>
);
const bareCtl = { flex: 1, minWidth: 0, height: "100%", border: 0, padding: 0, margin: 0, background: "transparent", outline: "none", boxShadow: "none" };
const bareSelect = { ...bareCtl, paddingRight: 18, WebkitAppearance: "none", appearance: "none", cursor: "pointer" };

const Counter = ({ value, max }) => (
  <p className="text-right text-[10px] text-gray-400 mt-0.5">
    {value}/{max}
  </p>
);

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-center justify-between gap-4 py-1">
    <div className="flex items-center gap-2.5 text-xs text-gray-500">
      <Icon size={14} className="text-gray-400" />
      {label}
    </div>
    <div className="text-xs font-medium text-gray-800 text-right">{children}</div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="min-w-0 px-3 first:pl-0 border-l border-gray-200 first:border-l-0">
    <p className="text-[10px] text-gray-500 mb-0.5">{label}</p>
    <p className="text-xs font-medium text-gray-800 truncate" title={String(value)}>
      {value}
    </p>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const formatPages = (pages) => {
  if (!pages) return "";
  const plural = pages !== 1 ? "s" : "";
  return ` • ${pages} page${plural}`;
};

export default function RequestSignaturePage() {
  const navigate = useNavigate();
  const currentStep = 0;

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const DRAFT_KEY = `request-signature-draft:${(user.email || "").toLowerCase()}`;

  const fileInputRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [docHash, setDocHash] = useState(null);
  const [showVault, setShowVault] = useState(false);

  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [deadline, setDeadline] = useState(toDateInput(daysFromNow(30)));
  const [reminder, setReminder] = useState("3");
  const [requireAll, setRequireAll] = useState(true);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Restore a saved draft (the fields only - the document itself has to be re-attached)
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (!d) return;
      if (d.title) {
        setTitle(d.title);
        setTitleTouched(true);
      }
      if (d.message) setMessage(d.message);
      if (d.deadline) setDeadline(d.deadline);
      if (d.reminder) setReminder(d.reminder);
      if (typeof d.requireAll === "boolean") setRequireAll(d.requireAll);
      setNotice("Draft restored. Please attach your document again.");
    } catch {
      /* ignore a corrupt draft */
    }
  }, []);

  /* ---------------- upload ---------------- */

  const processFile = async (file, fromVault = false, vaultFileId = null) => {
    if (!file) return;
    setError("");
    setNotice("");

    const ext = extOf(file.name);
    if (!ACCEPTED.includes(ext)) {
      setError(`Unsupported file type. Accepted formats: ${ACCEPTED.join(", ").toUpperCase()}.`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`File is larger than ${MAX_MB} MB.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_URL}/api/upload-document`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Upload failed");

      const buffer = await file.arrayBuffer();
      const [hash, pages] = await Promise.all([
        sha256Hex(buffer),
        ext === "pdf" ? countPdfPages(buffer) : Promise.resolve(null),
      ]);

      if (doc?.url) URL.revokeObjectURL(doc.url);

      // Same shape the next pages already expect (file / name / size / date / path / url)
      setDoc({
        file,
        name: data.file_name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
        date: new Date().toLocaleDateString(),
        path: data.file_path,
        url: URL.createObjectURL(file),
        pages,
        ext,
        fromVault,
        vaultFileId,
        uploadedAt: new Date(),
      });
      setDocHash(hash);
      if (!titleTouched) setTitle(prettyTitle(data.file_name || file.name));
    } catch (err) {
      console.error("Upload Error:", err);
      setError(err.message || "File upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const removeDoc = () => {
    if (doc?.url) URL.revokeObjectURL(doc.url);
    setDoc(null);
    setDocHash(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ---------------- footer actions ---------------- */

  const handleCancel = () => {
    removeDoc();
    setTitle("");
    setTitleTouched(false);
    setMessage(DEFAULT_MESSAGE);
    setDeadline(toDateInput(daysFromNow(30)));
    setReminder("3");
    setRequireAll(true);
    setError("");
    setNotice("");
    localStorage.removeItem(DRAFT_KEY);
  };

  const handleSaveDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, message, deadline, reminder, requireAll }));
      setError("");
      setNotice("Draft saved. The document itself isn't stored and will need to be attached again.");
    } catch {
      setError("Could not save the draft in this browser.");
    }
  };

  const handleNext = () => {
    setNotice("");
    if (uploading) return setError("Please wait for the upload to finish.");
    if (!doc) return setError("Please upload a document first.");
    if (!title.trim()) return setError("Please enter a request title.");
    if (!message.trim()) return setError("Please enter a message to recipients.");
    if (!deadline || deadline < toDateInput(new Date())) return setError("Please choose a signing deadline that isn't in the past.");

    setError("");
    localStorage.removeItem(DRAFT_KEY);
    navigate("/add-recipients", {
      state: {
        uploadedFile: doc,
        requestMeta: { title: title.trim(), message: message.trim(), deadline, reminder, requireAll },
      },
    });
  };

  const inputCls =
    "w-full h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-800 bg-white outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

  let statusMessage = null;
  if (error) {
    statusMessage = <span className="text-red-500">{error}</span>;
  } else if (notice) {
    statusMessage = <span className="text-green-600">{notice}</span>;
  }

  return (
    <div className="w-full space-y-3" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {/* STEPPER */}
      <div className="px-6 py-1">
        <div className="flex items-start">
          {STEPS.map((step, index) => {
            let circleClass = "bg-gray-100 text-gray-500";
            if (index < currentStep) {
              circleClass = "bg-green-500 text-white";
            } else if (index === currentStep) {
              circleClass = "bg-indigo-600 text-white";
            }

            return (
            <div key={step} className="flex-1 flex flex-col items-center relative">
              {index !== STEPS.length - 1 && (
                <div
                  className={`absolute top-4 h-[2px] ${index <= currentStep ? "bg-indigo-500" : "bg-gray-200"}`}
                  style={{ left: "calc(50% + 22px)", width: "calc(100% - 44px)" }}
                />
              )}
              <div
                className={`z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${circleClass}`}
              >
                {index < currentStep ? <Check size={14} /> : index + 1}
              </div>
              <p
                className={`mt-1.5 text-xs font-medium text-center ${
                  index === currentStep ? "text-indigo-600" : "text-gray-500"
                }`}
              >
                {step}
              </p>
            </div>
            );
          })}
        </div>
      </div>

      {/* SECURE VAULT PICKER: the unlocked file goes through the same upload as a browsed file */}
      <VaultPicker
        open={showVault}
        purpose="request"
        onClose={() => setShowVault(false)}
        onSelect={(picked) => processFile(picked.file, true, picked.vaultFileId)}
      />

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        {/* LEFT — upload */}
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl px-4 py-3 text-center transition ${
              dragOver ? "border-indigo-500 bg-indigo-50" : "border-indigo-200 bg-indigo-50/30"
            }`}
          >
            <div className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-1">
              <UploadCloud size={18} className="text-indigo-500" />
            </div>
            <p className="text-sm font-semibold text-gray-800">Drag &amp; Drop your document here</p>
            <p className="text-[11px] text-gray-400 my-1">or</p>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept={ACCEPTED.map((e) => `.${e}`).join(",")}
              onChange={(e) => processFile(e.target.files?.[0])}
            />
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 h-9 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg transition"
              >
                <FolderOpen size={14} />
                {uploading ? "Uploading..." : "Browse Files"}
              </button>
              <button
                type="button"
                onClick={() => setShowVault(true)}
                disabled={uploading}
                className="inline-flex items-center gap-2 h-9 px-6 bg-white border border-indigo-600 text-indigo-600 hover:bg-indigo-50 disabled:opacity-60 text-xs font-medium rounded-lg transition"
              >
                <Lock size={14} />
                Select from Secure Vault
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2.5 leading-relaxed">
              Accepted formats: PDF, DOCX, TXT, XLSX, PNG, JPG
              <br />
              Maximum file size: {MAX_MB} MB
            </p>
          </div>

          {doc && (
            <>
              <h3 className="text-sm font-semibold text-gray-800 mt-3 mb-1.5">Uploaded Document</h3>
              <div className="border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-8 h-10 rounded-md flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${
                      doc.ext === "pdf" ? "bg-red-500" : "bg-indigo-500"
                    }`}
                  >
                    {doc.ext ? doc.ext.toUpperCase().slice(0, 4) : <FileText size={14} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate" title={doc.name}>
                      {doc.name}
                      {doc.fromVault && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-medium">
                          Secure Vault
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {doc.size}
                      {formatPages(doc.pages)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0 text-xs">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700"
                  >
                    <RefreshCw size={13} /> Replace
                  </button>
                  <button type="button" onClick={removeDoc} className="flex items-center gap-1.5 text-gray-600 hover:text-red-500">
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg px-3 py-2 mt-2">
                <p className="text-xs font-semibold text-gray-800 mb-2">Document Details</p>
                <div className="flex items-center justify-between gap-3">
                  <div className="grid grid-cols-4 flex-1 min-w-0">
                    <Detail label="File Name" value={doc.name} />
                    <Detail label="File Size" value={doc.size} />
                    <Detail label="Pages" value={doc.pages ?? "—"} />
                    <Detail label="Uploaded On" value={formatUploaded(doc.uploadedAt)} />
                  </div>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 h-8 px-4 border border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg text-xs font-medium shrink-0 transition"
                  >
                    <Eye size={13} /> Preview
                  </a>
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT — details + security */}
        <div className="space-y-3">
          {/* Request details */}
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="flex items-center gap-2.5 mb-2">
              <FileText size={18} className="text-indigo-600" />
              <h3 className="text-sm font-semibold text-gray-900">Request Details</h3>
            </div>

            <div className="mb-2">
              <Label required>Request Title</Label>
              <input
                type="text"
                value={title}
                maxLength={TITLE_MAX}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setTitleTouched(true);
                  setError("");
                }}
                placeholder="e.g. Master Service Agreement"
                className={inputCls}
              />
              <Counter value={title.length} max={TITLE_MAX} />
            </div>

            <div className="mb-2">
              <Label required>Message to Recipients</Label>
              <textarea
                rows={2}
                value={message}
                maxLength={MESSAGE_MAX}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setError("");
                }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
              <Counter value={message.length} max={MESSAGE_MAX} />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <Label required>Signing Deadline</Label>
                <IconField icon={Calendar}>
                  <input
                    type="date"
                    value={deadline}
                    min={toDateInput(new Date())}
                    onChange={(e) => {
                      setDeadline(e.target.value);
                      setError("");
                    }}
                    className="text-sm text-gray-800"
                    style={bareCtl}
                  />
                </IconField>
              </div>
              <div>
                <Label>Reminder Frequency</Label>
                <IconField icon={Bell} chevron>
                  <select
                    value={reminder}
                    onChange={(e) => setReminder(e.target.value)}
                    className="text-sm text-gray-800"
                    style={bareSelect}
                  >
                    {REMINDERS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </IconField>
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={requireAll}
                onChange={(e) => setRequireAll(e.target.checked)}
                className="w-4 h-4 accent-indigo-600"
              />
              Require all recipients to sign
              <span title="The request is only complete once every selected recipient has finished their step.">
                <Info size={13} className="text-gray-400" />
              </span>
            </label>
          </div>

          {/* Security & trust */}
          <div className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="flex items-center gap-2.5 mb-2">
              <ShieldCheck size={18} className="text-indigo-600" />
              <h3 className="text-sm font-semibold text-gray-900">Security &amp; Trust</h3>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-1 divide-y divide-gray-100">
              <InfoRow icon={Lock} label="Signing Method">
                Hybrid: Post-Quantum + Classical
              </InfoRow>
              <InfoRow icon={UserCheck} label="Identity Verification">
                Digital ID / certificate
              </InfoRow>
              <InfoRow icon={Clock} label="Timestamp">
                Enabled
              </InfoRow>
              <InfoRow icon={ClipboardList} label="Audit Trail">
                Enabled
              </InfoRow>
              <InfoRow icon={Hash} label="Document Hash">
                {docHash ? (
                  <span title={docHash}>
                    SHA-256 · <span className="font-mono">{docHash.slice(0, 12)}…</span>
                  </span>
                ) : (
                  "SHA-256 (generated after upload)"
                )}
              </InfoRow>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={handleCancel}
          className="h-9 px-6 border border-indigo-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-medium transition"
        >
          Cancel
        </button>

        <div className="flex-1 min-w-[160px] text-center text-xs">
          {statusMessage}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveDraft}
            className="flex items-center gap-2 h-9 px-5 border border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg text-xs font-medium transition"
          >
            <Save size={14} /> Save Draft
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={uploading}
            className="flex items-center gap-2 h-9 px-5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg text-xs font-medium transition"
          >
            <ArrowRight size={14} /> Continue to Add Recipients
          </button>
        </div>
      </div>
    </div>
  );
}